import Dragula from '@/modules/dragula';
import _ from 'lodash';
import 'element-qsa-scope';
import store from '@/store';
import droplets from '@editor/droplets/droplets.config.json';
import { form } from '@editor/droplets/form/field-types';
import newDroplets from './new-droplets';
import nonDroplets from './dragula-config.json';

const config = Object.assign(droplets, nonDroplets);
const resetStyles = (el) => {
  const elements = el.querySelectorAll('[data-drag-reset="style"]');
  elements.forEach((node) => {
    node.style = null;
  });
};

const drzDragula = {
  drake: new Dragula({
    accepts(el, target) {
      let accepted = false;
      // get the type of dragged el
      let drpType = el.getAttribute('drp-type');
      if (drpType.match(/\./gi)) {
        drpType = drpType.split('.')[1];
      }
      // on hover over dragula container, get location
      const hoveredTarget = target.getAttribute('drp-level');
      // search drag / drop config & fetch allowed targets
      const droplet = config[drpType];
      if (!_.isUndefined(droplet)) {
        const targets = droplet.dropTargets;
        if (targets.includes(hoveredTarget)) {
          accepted = hoveredTarget;
        }
      }
      return accepted;
    },
    animate: {
      duration: 150,
      // don't animate in these containers
      noAnimateClass: ['drzSocialIcon-dragContainer'],
    },
    customGhost: true,
    moves(el, container, handle) {
      return handle.classList.contains('drzDrag-handle');
    },
    copy(el, source) {
      // allowed copy types
      if (window.drzzle.dragType === 'copy') {
        return true;
      }
      if (el.classList.contains('drzDroplet') &&
          el.parentNode.classList.contains('drzDroplets-column')) {
        // New droplet buttons in tabs
        return source === el.parentNode;
      } else if (el.classList.contains('drpBubble-form-fieldBtn') &&
          el.parentNode.classList.contains('drpBubble-newFields')) {
        // New form fields in bubble
        return source === el.parentNode;
      }
      return false;
    },
    // if we want to allow copying within the same container then we need to
    // set this to true however this causes copy bugs we'd need to solve
    copySortSource() {
      return window.drzzle.dragType === 'copy';
    },
    invalid(el) {
      const hint = el.classList.contains('drzSection-hint');
      return hint;
    },
    source: null,
  })
    .on('start', () => {
      drzDragula.dispatch('setDragging', true);
    })
    .on('cloned', (copy, item) => {
      // this is used because we inject style helpers to the nav
      // droplet but it adds this weird extra spacing when dragging
      // this droplet so we reset the styles to nothing for the cloned
      // ghost dragged elements
      const nav = 'droplet.navigation';
      if (copy.getAttribute('drp-type') === nav) {
        resetStyles(copy);
      }
      if (item.getAttribute('drp-type') === nav) {
        resetStyles(item);
      }
    })
    .on('drag', (el, source) => {
      drzDragula.dragEl = el;
      window.addEventListener('mousemove', drzDragula.autoScroll, false);
      drzDragula.dispatch('setVnodeDragId', source.getAttribute('vnode-strict-id'));

      let type = el.getAttribute('drp-type');
      if (type && type.match(/\./gi)) {
        type = type.split('.')[1];
      }

      drzDragula.source = source;
      // make sure to exclude <style> tags as they are counted as children
      drzDragula.oldIndex = [].indexOf.call(source.querySelectorAll(`:scope > ${drzDragula.ignoreClasses}`), el);

      // set any drag handles to dragging cursor
      const dropletHandle = el.querySelectorAll('.moveDroplet')[0];
      const sectionHandle = el.querySelectorAll('.moveSection')[0];
      const formBtnHandle = el.querySelectorAll('.drzDrag-handle')[0];
      const dropletBtn = el.querySelectorAll('.drzDropletBtn')[0];
      const cursor = '-webkit-grabbing';
      drzDragula.resetCursor(dropletHandle, cursor);
      drzDragula.resetCursor(sectionHandle, cursor);
      drzDragula.resetCursor(formBtnHandle, cursor);
      drzDragula.resetCursor(dropletBtn, cursor);

      // when a block droplet is in the blocks array and in a template, we want to
      // allow the user to move this block around in a page (using this template)
      // for easier rearraning
      if (dropletHandle && dropletHandle.classList.contains('templateBlock')) {
        drzDragula.dispatch('setGlobalDragItem', 'templateDroplet');
      }

      if ([
        'social-media-editor',
        'new-form-field',
        'preview-form-field',
        'in-bubble-item',
        'sectionBlock',
      ].includes(type)) {
        drzDragula.dispatch('setGlobalDragItem', 'noDragHelper');
      }

      // close any open droplet bubbles when starting to drag a droplet button
      const overlay = document.querySelectorAll('#drzBubble-overlay-container > .drpBubble-overlay')[0];
      if (!_.isUndefined(overlay) && el.classList.contains('drzDroplet')) {
        // this will trigger the vue method to close
        overlay.click();
      }

      // collapse droplets container once draggging anything
      drzDragula.dispatch('toggleDroplets', false);
      drzDragula.dispatch('setActiveDroplet', { droplet: {}, items: [] });

      // Show puddle column helpers when dragging
      const droppedArea = document.querySelectorAll('#sectionsArea')[0] || document.body;
      const puddleHelpers = droppedArea.querySelectorAll('div[drp-level="in-puddle-col"], div[drp-level="in-puddle-grid"]');
      const droplet = config[type];
      // we only need to show the column puddle helpers when a user is dragging a droplet
      // that is allowed inside of a column
      if (!_.isUndefined(droplet)) {
        if (droplet.dropTargets.includes('in-puddle-col') || droplet.dropTargets.includes('in-puddle-grid')) {
          puddleHelpers.forEach((container) => {
            container.classList.add('drzDroplet-puddleDropped');
          });
        }
      }
    })

    .on('drop', (el, target, source) => {
      // Change / reset any grabbing cursors on handles
      const defHandle = el.querySelectorAll('.drzDrag-handle')[0];
      const dropletHandle = el.querySelectorAll('.moveDroplet')[0];
      const sectionHandle = el.querySelectorAll('.moveSection')[0];
      const formBtnHandle = source.querySelectorAll('.drzDrag-handle')[0];
      const dropletBtn = source.querySelectorAll('.drzDropletBtn')[0];
      const cursor = '-webkit-grab';
      drzDragula.resetCursor(defHandle, cursor);
      drzDragula.resetCursor(dropletHandle, cursor);
      drzDragula.resetCursor(sectionHandle, cursor);
      drzDragula.resetCursor(formBtnHandle, cursor);
      drzDragula.resetCursor(dropletBtn, cursor);

      // drop item logic
      if (!_.isNull(target)) {
        // started & ended location look ups
        const started = source.getAttribute('drp-level');
        // const ended = target.getAttribute('drp-level');
        // Rule of thumb: If a new[Item]Index is coming from an
        // array that will have something removed, need to use
        //  [].indexOf.call and not the attribute of the el.

        // for vnode emitters
        const params = {};
        // always grab the started and ended _id's for everything
        const startedId = source.getAttribute('drp-id');
        const endedId = target.getAttribute('drp-id');

        if (started === 'in-droplet-btns' || started === 'in-new-formfield' || window.drzzle.dragType === 'copy') {
          // for brand new droplets (any type, and/or new form fields)
          const type = el.getAttribute('drp-type');
          const hasList = target.getAttribute('drp-list');
          // so far new things dropped are 'droplets' and form 'fields'
          let list = 'droplets';
          // if a new form field was dropped
          if (hasList) {
            list = hasList;
            params.field = el.getAttribute('field-type');
          }
          // make sure to exclude <style> tags as they are counted as children
          const newIndex = [].indexOf.call(target.querySelectorAll(`:scope > ${drzDragula.ignoreClasses}`), el);
          const vnode = drzDragula.getVnode(endedId).binding.value;
          params.items = vnode.items;
          params.droplet = vnode.droplet;
          params.oldIndex = drzDragula.oldIndex;
          if (window.drzzle.dragType === 'copy' && startedId === endedId) {
            // here we know a user is trying to copy a droplet inside the same container
            // in which case we need to treat this a little different than other copies
            params.newIndex = newIndex >= params.oldIndex ? newIndex + 1 : newIndex;
            params.copiedDroplet = params.items[drzDragula.oldIndex];
            params.bouncePage = vnode.bouncePage;
            params.sameParent = true;

            drzDragula.removeElement(el);

            const evt = new CustomEvent('copyDroplet', { detail: params });
            window.dispatchEvent(evt);
          } else {
            // this scenario could be dropping new droplets or copying to different containers

            // payload for emitter
            params.list = list;
            params.endedId = endedId;
            params.type = type;
            params.newIndex = newIndex;

            // remove ghost el from DOM
            drzDragula.removeElement(el);

            // send off to store
            if (window.drzzle.dragType !== 'copy') {
              const p = drzDragula.getPayload(params);
              drzDragula.dispatch('dropNewDroplet', p).then(() => {
                // check for any callbacks to run
                drzDragula.emitToVnode(params);
                if (started === 'in-droplet-btns') {
                  const evt = new CustomEvent('dropNewDroplet', { detail: { ...params, drp: p.drp } });
                  window.dispatchEvent(evt);
                }
              });
            } else {
              // this is in the event a droplet on the page is being copied
              params.startedItems = drzDragula.getVnode(startedId).binding.value.items;
              params.oldIndex = drzDragula.oldIndex;
              params.copiedDroplet = params.startedItems[drzDragula.oldIndex];
              const evt = new CustomEvent('copyDroplet', { detail: params });
              window.dispatchEvent(evt);
            }
          }
        } else {
          const drpId = el.getAttribute('drp-id');
          // generally moving droplets around
          const oldIndex = drzDragula.oldIndex;
          // make sure to exclude <style> tags as they are counted as children
          const newIndex = [].indexOf.call(target.querySelectorAll(`:scope > ${drzDragula.ignoreClasses}`), el);
          // drp-lists's are containers that only allow rearraning
          // within themselves. (ie: items inside edit bubbles like accordion,
          // tabs, image gallery bubbles)
          const hasList = source.getAttribute('drp-list');
          // represents the array of things being moved
          let list = 'droplets';
          // if any bubble items are dropped around
          if (hasList) {
            list = hasList;
          }

          // grab target and source arrays to splice from
          const getVnodeFn = store.getters.vnodeDragId ? 'getStrictVnode' : 'getVnode';
          params.sourceItems = drzDragula[getVnodeFn](startedId).binding.value.items;
          const droplet = params.sourceItems[oldIndex];
          if (startedId === endedId) {
            params.sameParent = true;
            params.targetItems = params.sourceItems;
          } else {
            params.targetItems = drzDragula[getVnodeFn](endedId).binding.value.items;
          }

          // payload for emitter
          params.list = list;
          params._id = drpId;
          params.startedId = startedId;
          params.endedId = endedId;
          params.oldIndex = oldIndex;
          params.newIndex = newIndex;
          params.droplet = droplet;

          // send off to store
          drzDragula.dispatch('moveDroplet', params).then(() => {
            // check for any callbacks to run
            drzDragula.emitToVnode(params);
            const evt = new CustomEvent('moveDroplet', { detail: params });
            window.dispatchEvent(evt);
          });
        }
      }
    })

    .on('over', (el, container) => {
      drzDragula.overContainer = container;
      const target = container.getAttribute('drp-level');
      if (target === 'in-section') {
        container.classList.add('drzDroplet-containerNoDrps');
        container.classList.add('drzDroplet-containerHover');
        container.parentNode.classList.add('focusedSection');
      }
      if (target.match(/in-puddle-/gi)) {
        container.classList.add('dragula-colhelper');
      }
    })

    .on('out', (el, container) => {
      drzDragula.overContainer = null;
      const target = container.getAttribute('drp-level');
      if (target === 'in-section') {
        // if section container is not empty, remove helpers on drag out.
        container.classList.remove('drzDroplet-containerHover');
        const drps = container.querySelectorAll(':scope > div:not(.gu-transit):not(.drzSection-hint)');
        drzDragula.removeHints(drps, container);
      }
      if (target.match(/in-puddle-/gi)) {
        container.classList.remove('dragula-colhelper');
      }
    })

    .on('dragend', (el) => {
      drzDragula.dragEl = null;
      drzDragula.overContainer = null;
      window.removeEventListener('mousemove', drzDragula.autoScroll, false);
      window.drzzle.dragType = null;
      drzDragula.dispatch('setDragging', false);
      const source = drzDragula.source;
      // reset draghandle cursors
      const defHandle = el.querySelectorAll('.drzDrag-handle')[0];
      const dropletHandle = el.querySelectorAll('.moveDroplet')[0];
      const sectionHandle = el.querySelectorAll('.moveSection')[0];
      const cursor = '-webkit-grab';
      drzDragula.resetCursor(defHandle, cursor);
      drzDragula.resetCursor(dropletHandle, cursor);
      drzDragula.resetCursor(sectionHandle, cursor);

      drzDragula.dispatch('setGlobalDragItem', false);
      drzDragula.dispatch('setVnodeDragId', null);

      // need to remove from source this way since 'dragend' does not take a source param
      if (source.getAttribute('drp-level').match(/in-new-formfield/gi)) {
        const handles = source.querySelectorAll('.drzDrag-handle');
        _.forEach(handles, (handle) => {
          drzDragula.resetCursor(handle, cursor);
        });
      }

      // remove col helpers if no children
      const droppedArea = document.querySelectorAll('#sectionsArea')[0] || document.body;
      const puddleHelpers = droppedArea.querySelectorAll('div[drp-level="in-puddle-col"], div[drp-level="in-puddle-grid"]');
      puddleHelpers.forEach((container) => {
        if (container.children.length > 0) {
          container.classList.remove('drzDroplet-puddleDropped');
        }
      });

      const container = el.parentNode;
      // if section container is not empty, remove helpers on drag end.
      if (!_.isNull(container) && container.classList.contains('container') &&
          !el.classList.contains('drzDragulaSection')) {
        const drps = container.querySelectorAll(':scope > div:not(.gu-transit):not(.drzSection-hint)');
        drzDragula.removeHints(drps, container);
      }
    }),
  dragEl: null,
  overContainer: null,
  mouseStart: 0,
  scrollAmount: 8,
  direction: null,
  autoScroll: () => {
    // TODO if screen is certain small height or smaller, make threshold smaller
    // determine if mouse is moving up or down
    if (event.pageY < drzDragula.mouseStart) {
      drzDragula.direction = 'up';
    }
    if (event.pageY > drzDragula.mouseStart) {
      drzDragula.direction = 'down';
    }
    drzDragula.mouseStart = event.pageY;
    // when reaching the top edge of the screen
    if (event.pageY < (window.scrollY) + 165 && drzDragula.direction === 'up') {
      window.scrollBy(0, -(drzDragula.scrollAmount));
    }
    // when reaching the bottom edge of the screen
    if (event.pageY > (window.innerHeight + window.scrollY) - 120 && drzDragula.direction === 'down') {
      window.scrollBy(0, drzDragula.scrollAmount);
    }
  },
  getNum(val) {
    let num = val;
    if (!_.isUndefined(val) && !_.isNull(val)) {
      num = ~~(val);
    }
    return num;
  },
  removeHints(drps, el) {
    if (drps.length > 0) {
      el.classList.remove('drzDroplet-containerNoDrps');
      el.parentNode.classList.remove('focusedSection');
    }
  },
  removeElement(node) {
    if (node && node.parentNode) {
      node.parentNode.removeChild(node);
    }
  },
  resetCursor(el, cursor) {
    if (!_.isUndefined(el)) {
      el.style.cursor = cursor;
    }
  },
  oldIndex: null,
  ignoreClasses: '*:not(style)',
  startCursor: '',
  guid() {
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }
    return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
  },
  getFormField(type) {
    let field = _.find(form.fields, { type });
    field = _.cloneDeep(field);
    field._id = this.guid();
    return field;
  },
  getPayload(z) {
    let drp;
    if (z.list === 'droplets') {
      const data = newDroplets[z.type]();
      drp = _.cloneDeep(data);
    } else if (z.list === 'fields') {
      drp = this.getFormField(z.field);
    }
    return {
      drp,
      list: z.list,
      _id: z.endedId,
      newIndex: z.newIndex,
      items: z.items,
    };
  },
  dispatch(action, payload) {
    return new Promise((resolve) => {
      if (store.dispatch(action, payload)) {
        resolve();
      }
    });
  },
  // [Custom Emitter] - This is used to pass data to components via the vnode interface,
  // so that we can manipulate data & change state from this directive.
  emitToVnode(params) {
    const vnodes = this.vnodes;
    return _.forEach(vnodes, (vnode) => {
      const v = vnode.vnode;
      if (params && v.data.attrs && v.data.attrs['drp-id']) {
        const vId = v.data.attrs['drp-id'];
        const _id = params.endedId;
        // this will check & run custom fn's from directive bindings
        if (_id === vId && vnode.binding.value.fn) {
          // init the fn param from directive
          v.context[vnode.binding.value.fn](params);
        }
      }
    });
  },
  getVnode(endedId) {
    let vn = null;
    const vnodes = this.vnodes;
    _.forEach(vnodes, (vnode) => {
      const v = vnode.vnode;
      if (v.data.attrs && v.data.attrs['drp-id']) {
        const vId = v.data.attrs['drp-id'];
        // this will check & run custom fn's from directive bindings
        if (endedId === vId) {
          vn = vnode;
        }
      }
    });
    return vn;
  },
  getStrictVnode() {
    let vn = { binding: { value: {} } };
    const vnodes = this.vnodes;
    _.forEach(vnodes, (vnode) => {
      const v = vnode.vnode;
      if (v.data.attrs && v.data.attrs['vnode-strict-id']) {
        const strictId = v.data.attrs['vnode-strict-id'];
        if (store.getters.vnodeDragId === strictId) {
          vn = vnode;
        }
      }
    });
    return vn;
  },
};

export { drzDragula }; // eslint-disable-line



// WEBPACK FOOTER //
// ./src/directives/dragula.js